import { Play, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * SAB-927: the control that starts the draft.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * From the routing cutover (dce0457, 2026-08-27 21:06 UTC) until this component landed,
 * NO NFL league could have its draft started from the UI. The only caller of
 * `nfl-initialize-draft` was NFLDraftPage.tsx:1152, inside a `pending`-only branch of a
 * room that rule (e) serves ONLY for `in_progress` and `paused` -- unreachable by
 * construction. The last draft ever started was five hours before that commit; fourteen
 * leagues accumulated at pending behind it over the following three days.
 *
 * ── PLACEMENT: A TOOLBAR, NOT AN EMPTY STATE ────────────────────────────────
 *
 * It goes in the centre toolbar on desktop and the panel toolbar on mobile, because both
 * render OUTSIDE their room's view switch. Hanging it off the players-table empty state --
 * the obvious spot, since that is where the "not started" copy lives -- would have hidden
 * it whenever the commissioner had BOARD selected on desktop, or any tab but PLAYERS on
 * mobile. Rebuilding a control that was lost to a view condition, behind a view condition,
 * is the one shape this fix must not take.
 *
 * ── THE REASON IS TEXT, NOT A TOOLTIP ───────────────────────────────────────
 *
 * Learned in CommishMenu (see its header): a disabled control receives no pointer events,
 * so the browser never registers a hover and a native `title` never fires. Wiring the
 * explanation to a mechanism the disabled state suppresses is how a blocked user is left
 * with a dead button and no reason. So the reason renders as visible text next to the
 * button, and is bound to it with aria-describedby for anyone not reading the pixels.
 *
 * ── NO CONFIRMATION DIALOG, DELIBERATELY ────────────────────────────────────
 *
 * The legacy button had none, and this restores that button rather than redesigning it.
 * Worth revisiting on its own ticket -- initializing a draft is cancel-and-rebuild rather
 * than undo, and in a slow league it starts an 8-hour clock -- but a behaviour change
 * bundled into an outage fix is a behaviour change nobody reviewed.
 */

export interface StartDraftControlProps {
  /** From resolveStartDraftGate: null when startable, otherwise why not. */
  reason: string | null;
  /** True while the initialize request is in flight. */
  starting: boolean;
  onStart: () => void;
  className?: string;
}

export default function StartDraftControl({
  reason, starting, onStart, className,
}: StartDraftControlProps) {
  const blocked = reason !== null;

  return (
    <div
      data-testid="start-draft-control"
      data-blocked={String(blocked)}
      className={cn('flex flex-none items-center gap-2.5', className)}
    >
      <Button
        data-testid="start-draft-button"
        onClick={onStart}
        // `starting` is the in-flight guard; `blocked` is the gate. Kept separate so a
        // double-click cannot fire a second initialize while the first is still running --
        // that would race two pick-row builds against the same league.
        disabled={blocked || starting}
        aria-describedby={blocked ? 'start-draft-reason' : undefined}
        className="h-9 bg-primary hover:bg-primary/90 font-display text-[12px] font-bold uppercase tracking-[0.04em]"
      >
        {starting
          ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          : <Play className="h-4 w-4 mr-1.5" />}
        {starting ? 'Starting…' : 'Start Draft'}
      </Button>

      {blocked && (
        <p
          id="start-draft-reason"
          data-testid="start-draft-reason"
          className="text-[11px] leading-tight text-amber-400 max-w-[15rem]"
        >
          {reason}
        </p>
      )}
    </div>
  );
}
